import { useState } from "react";
import {
  Modal,
  Typography,
  Form,
  Grid,
  Input,
  Select,
  Button,
  Space,
} from "antd";
import { UserInfo } from "../../interface";
import { updateUserAlias, updateUserRole } from "../../apiBridge";

interface Props {
  isOpen: boolean;
  user?: UserInfo;
  controller: React.Dispatch<React.SetStateAction<boolean>>;
  reloadController: React.Dispatch<React.SetStateAction<boolean>>;
  resetController: React.Dispatch<React.SetStateAction<boolean>>;
  isOwner: boolean;
}

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const onFinishFailed = (errorInfo: any) => {
  console.log("Failed:", errorInfo);
};

const EditUserModal: React.FC<Props> = ({
  isOpen,
  user,
  controller,
  reloadController,
  resetController,
  isOwner,
}) => {
  const screens = useBreakpoint();

  const [loadingState, setLoadingState] = useState(false);

  const onFinishEditUser = async (values: any) => {
    if (user) {
      setLoadingState(true);
      const [res1, res2] = await Promise.all([
        updateUserAlias({ id: user.id, alias: values.alias }),
        updateUserRole({ id: user.id, role: values.role }),
      ]);
      setLoadingState(false);
      if (res1.status === 200 && res2.status === 200) {
        reloadController(true);
        controller(false);
      }
    }
  };

  const triggerResetPWModal = () => {
    resetController(true);
    controller(false);
  };

  return (
    <Modal
      footer={null}
      maskClosable={false}
      closeIcon={false}
      centered
      open={isOpen && user !== undefined}
      width={420}
      destroyOnClose={true}
    >
      <Title level={4}>Edit User</Title>
      <Form
        name="edituserform"
        layout="vertical"
        onFinish={onFinishEditUser}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        initialValues={{
          role: user?.role,
          username: user?.username,
          alias: user?.alias,
        }}
      >
        <Form.Item
          label={screens.md ? "Username" : ""}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Username is used by users to login to the portal
            </Text>
          }
          name="username"
          rules={[{ required: true, message: "Please input a username!" }]}
        >
          <Input placeholder="Username" disabled />
        </Form.Item>

        <Form.Item
          label={screens.md ? "Display Name" : ""}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Display name is shown in-game
            </Text>
          }
          name="alias"
          rules={[
            {},
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || value === "") {
                  return Promise.reject(new Error("Please enter a display name."));
                }

                if (value.trim() !== value) {
                  return Promise.reject(new Error("The display name cannot have spaces before or after it."))
                }

                if (value.length > 25) {
                  return Promise.reject(new Error("The display name must be shorter than 25 characters."))
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="Display Name" />
        </Form.Item>

        <Form.Item
          label={screens.md ? "Role" : ""}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Role of user in the organisation
            </Text>
          }
          name="role"
          rules={[{ required: true, message: "Please select a role!" }]}
        >
          <Select disabled={!isOwner}>
            <Select.Option key="user" value="User">
                Player <span className="text-muted">(Able to view personal dashboard)</span>
            </Select.Option>
            <Select.Option key="gamemaster" value="GameMaster">
                GM <span className="text-muted">(Able to manage players and host matches)</span>
            </Select.Option>
          </Select>
        </Form.Item>
        <div style={{ marginBottom: 20 }}>
          <Button type="primary" ghost onClick={triggerResetPWModal}>
            Reset Password
          </Button>
        </div>
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={() => controller(false)}>Cancel</Button>

            <Form.Item style={{ margin: 0 }}>
              <Button type="primary" htmlType="submit" loading={loadingState}>
                Save
              </Button>
            </Form.Item>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
